import React, { createContext, useState, useContext, useEffect } from 'react';
import api, { authAPI, athletesAPI } from '../api';
import {getTranslatedError} from "../utils/errorMessage";
import { useTranslation } from '../i18n';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Проверяем, не гость ли
    const guest = localStorage.getItem('guest');
    if (guest === 'true') {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      setUser(userData);
      setAthlete(null);
      setIsAdmin(false);
      setIsGuest(true);
      setLoading(false);
      return;
    }

    // Проверяем обычную авторизацию
    const token = localStorage.getItem('access_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await athletesAPI.me();
      setAthlete(response.data);
      setUser({
        id: response.data.id,
        username: response.data.name || 'User',
        is_staff: response.data.access_level === 30,
        access_level: response.data.access_level,
      });
      setIsAdmin(response.data.access_level === 30);
      setIsGuest(false);
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await authAPI.login(username, password);

      localStorage.setItem('access_token', response.data.access_token);
      localStorage.removeItem('guest');

      setAthlete(response.data.athlete);
      setUser({
        id: response.data.user.id,
        username: response.data.user.username,
        is_staff: response.data.user.is_staff,
        access_level: response.data.athlete?.access_level || 10,
      });
      setIsAdmin(response.data.athlete?.access_level === 30);
      setIsGuest(false);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Ошибка входа'
      };
    }
  };

  // Гостевой вход
 const guestLogin = () => {
    const guestUser = {
      id: 'guest',
      name: 'Гость',
      username: 'guest',
      is_staff: false,
      is_guest: true,
      access_level: 0,
    };

    localStorage.setItem('guest', 'true');
    localStorage.setItem('user', JSON.stringify(guestUser));

    setUser(guestUser);
    setAthlete(null);
    setIsAdmin(false);
    setIsGuest(true);

    // Навигация — на стороне вызывающего компонента (см. LoginPage.handleGuestLogin),
    // как и у обычного login(). Раньше здесь было window.location.href = '/',
    // жёсткая перезагрузка, которая перебивала SPA-навигацию и уводила на /login.
  };
const register = async (username, password, inviteCode) => {
  try {
    const response = await authAPI.register(username, password, inviteCode);

    localStorage.setItem('access_token', response.data.access_token);
    localStorage.removeItem('guest');

    setAthlete(response.data.athlete);
    setUser({
      id: response.data.user.id,
      username: response.data.user.username,
      is_staff: response.data.user.is_staff,
      access_level: response.data.athlete?.access_level || 10,
    });
    setIsAdmin(response.data.athlete?.access_level === 30);
    setIsGuest(false);

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || 'Ошибка регистрации'
    };
  }
};
  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('guest');
    setUser(null);
    setAthlete(null);
    setIsAdmin(false);
    setIsGuest(false);
  };

  const updateAthlete = async (data) => {
    try {
      const response = await api.patch('/athletes/me/update/', data);
      setAthlete(prev => ({ ...prev, ...response.data }));
      return { success: true };
    } catch (error) {
      const errorData = error.response?.data;
      let rawMessage = error.message;

      if (errorData) {
        if (errorData.error) {
          rawMessage = errorData.message || errorData.error;
        } else {
          const firstField = Object.keys(errorData)[0];
          const firstMessage = Array.isArray(errorData[firstField])
            ? errorData[firstField][0]
            : errorData[firstField];
          if (firstMessage) rawMessage = firstMessage;
        }
      }

      return { success: false, error: getTranslatedError(rawMessage, t) };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      athlete,
      isAdmin,
      isGuest,
      loading,
      login,
      register,
      guestLogin,
      logout,
      updateAthlete,
      checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};